import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deactivateConversation, getConversations, getOrCreateChildCareTeamConversation } from '../../services/messaging';
import { getAllApprovedTeamMembers, getChildCareTeam } from '../../services/childAccessService';
import {
  canCreateCustomChildChat,
  canOpenDefaultChildChat,
  filterChildChatParticipants,
  getChildRoleForMessaging,
  getManageableMessagingChildren,
} from '../../services/messaging/participantAccess';

const DEFAULT_MOBILE_VIEW = 'conversations';
const THREAD_MOBILE_VIEW = 'thread';
const CHILD_SPECIFIC_MODE = 'child_specific';
const ALL_CONTACTS_MODE = 'all_contacts';

const isDefaultCareTeamConversation = (conversation = {}) => (
  conversation.conversationType === 'child_care_team'
  || /care team/i.test(conversation.title || '')
);

const getConversationSortTime = (conversation = {}) => {
  const updatedAt = conversation.updatedAt ? new Date(conversation.updatedAt).getTime() : 0;
  const createdAt = conversation.createdAt ? new Date(conversation.createdAt).getTime() : 0;
  return Math.max(updatedAt, createdAt, 0);
};

const dedupeDefaultCareTeamConversations = (conversations = []) => {
  const canonicalByChildId = new Map();
  const customConversations = [];

  conversations.forEach((conversation) => {
    if (!conversation?.childId || !isDefaultCareTeamConversation(conversation)) {
      customConversations.push(conversation);
      return;
    }

    const existing = canonicalByChildId.get(conversation.childId);
    const isDeterministicDefault = conversation.id === `careTeam_${conversation.childId}`;
    const existingIsDeterministicDefault = existing?.id === `careTeam_${conversation.childId}`;
    if (
      !existing
      || isDeterministicDefault
      || (!existingIsDeterministicDefault && getConversationSortTime(conversation) > getConversationSortTime(existing))
    ) {
      canonicalByChildId.set(conversation.childId, conversation);
    }
  });

  return [...canonicalByChildId.values(), ...customConversations]
    .sort((left, right) => getConversationSortTime(right) - getConversationSortTime(left));
};

export const useMessagesPageState = ({
  userId,
  isMobile = false,
  selectedChildId = null,
  selectedChild = null,
  childrenWithAccess = [],
  contextChildId = null,
  contextChild = null,
  openChildChat = false,
  initialSelectedConversationId = null,
  initialMobileView = DEFAULT_MOBILE_VIEW,
}) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(initialSelectedConversationId || null);
  const [loading, setLoading] = useState(true);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [mobileView, setMobileView] = useState(initialMobileView);
  const [messagingMode, setMessagingMode] = useState(contextChildId ? CHILD_SPECIFIC_MODE : ALL_CONTACTS_MODE);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [contactsByChildId, setContactsByChildId] = useState({});
  const [hasOpenedChildChat, setHasOpenedChildChat] = useState(false);
  const [openingChildChat, setOpeningChildChat] = useState(openChildChat && Boolean(contextChildId));
  const attemptedChildChatKeyRef = useRef(null);

  const contextChildRole = useMemo(
    () => getChildRoleForMessaging(childrenWithAccess, contextChildId),
    [childrenWithAccess, contextChildId]
  );
  const manageableChildren = useMemo(
    () => getManageableMessagingChildren(childrenWithAccess),
    [childrenWithAccess]
  );
  const canOpenChildCareTeamChat = Boolean(
    contextChildId
      ? canOpenDefaultChildChat(contextChildRole)
      : false
  );
  const canStartConversation = Boolean(
    contextChildId
      ? canCreateCustomChildChat(contextChildRole)
      : manageableChildren.length > 0
  );

  const loadConversations = useCallback(async ({ background = false } = {}) => {
    if (!userId) return;

    try {
      if (!background) setLoading(true);
      const result = await getConversations(userId, {
        childId: contextChildId || selectedChild?.id || selectedChildId || null,
        limit: 50,
      });

      if (result.success) {
        const nextConversations = dedupeDefaultCareTeamConversations(result.conversations || []);
        setConversations(nextConversations);

        if (selectedConversationId) {
          const match = nextConversations.find((conversation) => conversation.id === selectedConversationId);
          if (match) {
            setSelectedConversation(match);
            if (isMobile) {
              setMobileView(THREAD_MOBILE_VIEW);
            }
          }
        }
      } else {
        console.error('Failed to load conversations:', result.error);
        if (!background) setConversations([]);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      if (!background) setConversations([]);
    } finally {
      if (!background) setLoading(false);
    }
  }, [contextChildId, isMobile, selectedChild?.id, selectedChildId, selectedConversationId, userId]);

  const loadAvailableContacts = useCallback(async () => {
    if (!userId || !childrenWithAccess) return;

    try {
      let contacts = [];

      if (messagingMode === CHILD_SPECIFIC_MODE && contextChildId) {
        contacts = await getChildCareTeam(contextChildId);
      } else {
        const userChildIds = manageableChildren.map((child) => child.id);
        contacts = await getAllApprovedTeamMembers(userChildIds);
      }

      const filteredContacts = filterChildChatParticipants(contacts, userId);
      setAvailableContacts(filteredContacts);
    } catch (error) {
      console.error('Error loading available contacts:', error);
      setAvailableContacts([]);
    }
  }, [childrenWithAccess, contextChildId, manageableChildren, messagingMode, userId]);

  const loadContactsByChild = useCallback(async () => {
    if (!userId || !manageableChildren?.length) {
      setContactsByChildId({});
      return;
    }

    try {
      const contactEntries = await Promise.all(
        manageableChildren.map(async (child) => {
          try {
            const contacts = await getChildCareTeam(child.id);
            const filteredContacts = filterChildChatParticipants(contacts, userId);
            return [child.id, filteredContacts];
          } catch (error) {
            console.error(`Error loading care team for child ${child.id}:`, error);
            return [child.id, []];
          }
        })
      );

      setContactsByChildId(Object.fromEntries(contactEntries));
    } catch (error) {
      console.error('Error loading contacts by child:', error);
      setContactsByChildId({});
    }
  }, [manageableChildren, userId]);

  const handleConversationSelect = useCallback((conversation) => {
    setSelectedConversation(conversation);
    setSelectedConversationId(conversation?.id || null);

    if (isMobile) {
      setMobileView(THREAD_MOBILE_VIEW);
    }
  }, [isMobile]);

  const handleConversationCreated = useCallback((newConversation) => {
    const normalizedConversation = newConversation
      ? {
          ...newConversation,
          id: newConversation.conversationId || newConversation.id,
        }
      : null;

    setConversations((prev) => normalizedConversation
      ? dedupeDefaultCareTeamConversations([normalizedConversation, ...prev])
      : prev);
    setSelectedConversation(normalizedConversation);
    setSelectedConversationId(normalizedConversation?.id || null);
    setShowNewConversation(false);

    if (isMobile) {
      setMobileView(THREAD_MOBILE_VIEW);
    }
  }, [isMobile]);

  const handleDeactivateConversation = useCallback(async (conversation) => {
    if (!conversation?.id || !userId) {
      return { success: false, error: 'Conversation is missing required data' };
    }

    const result = await deactivateConversation(conversation.id, userId);
    if (!result.success) {
      console.error('Failed to archive conversation:', result.error);
      return result;
    }

    setConversations((prev) => prev.filter((item) => item.id !== conversation.id));

    if (selectedConversation?.id === conversation.id) {
      setSelectedConversation(null);
      setSelectedConversationId(null);
      if (isMobile) {
        setMobileView(DEFAULT_MOBILE_VIEW);
      }
    }

    return result;
  }, [isMobile, selectedConversation?.id, userId]);

  const handleModeChange = useCallback((newMode) => {
    setMessagingMode(newMode);
    setSelectedConversation(null);
    setSelectedConversationId(null);

    if (isMobile) {
      setMobileView(DEFAULT_MOBILE_VIEW);
    }
  }, [isMobile]);

  const resetToConversationList = useCallback(() => {
    setMobileView(DEFAULT_MOBILE_VIEW);
    setSelectedConversation(null);
  }, []);

  useEffect(() => {
    if (contextChildId) {
      setMessagingMode(CHILD_SPECIFIC_MODE);
    }
  }, [contextChildId]);

  useEffect(() => {
    if (!openChildChat || !contextChildId || !userId) {
      attemptedChildChatKeyRef.current = null;
      return;
    }

    const nextKey = `${userId}:${contextChildId}`;
    if (attemptedChildChatKeyRef.current !== nextKey) {
      attemptedChildChatKeyRef.current = nextKey;
      setHasOpenedChildChat(false);
      setOpeningChildChat(true);
    }
  }, [contextChildId, openChildChat, userId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    loadAvailableContacts();
  }, [loadAvailableContacts]);

  useEffect(() => {
    loadContactsByChild();
  }, [loadContactsByChild]);

  useEffect(() => {
    if (!openChildChat || hasOpenedChildChat || !contextChildId || !userId) {
      return;
    }

    if (!canOpenChildCareTeamChat && loading) {
      return;
    }

    const openChildConversation = async () => {
      try {
        setOpeningChildChat(true);

        if (!canOpenChildCareTeamChat) {
          const existingConversation = conversations.find((conversation) => (
            conversation?.childId === contextChildId && isDefaultCareTeamConversation(conversation)
          ));

          if (existingConversation) {
            setSelectedConversation(existingConversation);
            setSelectedConversationId(existingConversation.id || null);
            if (isMobile) {
              setMobileView(THREAD_MOBILE_VIEW);
            }
          }

          setHasOpenedChildChat(true);
          return;
        }

        const result = await getOrCreateChildCareTeamConversation({
          childId: contextChildId,
          childName: contextChild?.name,
          createdBy: userId,
        });

        if (result.success) {
          setSelectedConversationId(result.conversationId || null);
          const fallbackConversation = result.conversation
            ? {
                ...result.conversation,
                id: result.conversationId,
              }
            : null;
          const conversation = fallbackConversation || null;

          if (conversation) {
            setSelectedConversation(conversation);
            if (isMobile) {
              setMobileView(THREAD_MOBILE_VIEW);
            }
          }
        } else {
          console.error('Failed to open child conversation:', result.error);
        }
      } catch (error) {
        console.error('Failed to open child conversation:', error);
      } finally {
        setHasOpenedChildChat(true);
        setOpeningChildChat(false);
      }
    };

    openChildConversation();
  }, [canOpenChildCareTeamChat, contextChild?.name, contextChildId, conversations, hasOpenedChildChat, isMobile, loading, openChildChat, userId]);

  return {
    availableContacts,
    canOpenChildCareTeamChat,
    canStartConversation,
    conversations,
    contextChildRole,
    contactsByChildId,
    handleConversationCreated,
    handleDeactivateConversation,
    handleConversationSelect,
    handleModeChange,
    hasOpenedChildChat,
    loadConversations,
    loading,
    messagingMode,
    mobileView,
    manageableChildren,
    openingChildChat,
    resetToConversationList,
    selectedConversation,
    selectedConversationId,
    setShowNewConversation,
    showNewConversation,
  };
};

export default useMessagesPageState;
